import type { DocumentRecord, DocumentRepository } from "@/domain";

import type { DatabaseInvalidationPublisher } from "./broadcast-channel";
import type { MarginaliaDatabase } from "./database";
import { toStorageWriteError } from "./storage-errors";

export class DexieDocumentRepository implements DocumentRepository {
  constructor(
    private readonly db: MarginaliaDatabase,
    private readonly invalidation?: DatabaseInvalidationPublisher,
  ) {}

  get(id: string): Promise<DocumentRecord | undefined> {
    return this.db.documents.get(id);
  }

  findBySha256(sha256: string): Promise<DocumentRecord | undefined> {
    return this.db.documents.where("sha256").equals(sha256).first();
  }

  listRecent(): Promise<DocumentRecord[]> {
    return this.db.documents.orderBy("lastOpenedAt").reverse().toArray();
  }

  async put(record: DocumentRecord): Promise<void> {
    try {
      await this.db.documents.put(record);
    } catch (error) {
      throw toStorageWriteError(error, "save document metadata");
    }

    this.invalidation?.publish({
      type: "document-changed",
      documentId: record.id,
    });
  }

  async delete(id: string): Promise<void> {
    try {
      await this.db.transaction(
        "rw",
        [
          this.db.documents,
          this.db.pdfBlobs,
          this.db.annotations,
          this.db.readingStates,
          this.db.pageTextCache,
        ],
        async () => {
          await Promise.all([
            this.db.pdfBlobs.delete(id),
            this.db.annotations.where("documentId").equals(id).delete(),
            this.db.readingStates.delete(id),
            this.db.pageTextCache.where("documentId").equals(id).delete(),
          ]);
          await this.db.documents.delete(id);
        },
      );
    } catch (error) {
      throw toStorageWriteError(error, "delete a document and its local data");
    }

    this.invalidation?.publish({ type: "document-deleted", documentId: id });
  }
}
